import { useReactiveVar } from '@apollo/client';
import React, { useState, useEffect } from 'react';

import TableHeader from '@/components/TableHeader';
import Table from '@/components/Table';
import { TableRow, TableCell } from '@/elements/Table';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import TextStack from '@/components/TextStack';
import useMediaQuery from '@/hooks/useMediaQuery';
import { currentAgencyVar } from '@/util/apollo/cache';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { format } from 'date-fns';
import Chip from '@/components/Chip';
import Link from '@/elements/Link';
import IconicButton from '@/components/IconicButton';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import ReportModal from './ReportModal';
import TextInput from '@/components/TextInput';
import Pagination from '@/components/Pagination';

const HEADER_FIELDS = [
  { name: 'ID', style: { width: '10%' } },
  { name: 'WORKER ID', style: { width: '25%', color: 'black' } },
  { name: 'PREVIOUS STATUS', style: { width: '15%', color: 'black' } },
  { name: 'NEW STATUS', style: { width: '15%', color: 'black' } },
  { name: 'NOTES', style: { width: '25%', color: 'black' } },
  { name: 'DATE', style: { width: '20%', color: 'black' } },
  { name: 'ACTIONS', style: { width: '10%', color: 'black' } },
];

type ZealCheckLog = {
  id: string;
  worker_id: number;
  report_data: any;
  previous_status: string;
  new_status: string;
  notes: string;
  created_at: string;
  updated_at: string;
  worker: {
    zeal_employee_id: string;
    user: {
      first_name: string;
      middle_name: string;
      last_name: string;
    };
  };
};

type PaginationData = {
  total_count: number;
  current_page: number;
  per_page: number;
  total_pages: number;
};

const getStatusChip = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'passed':
    case 'approved':
    case 'completed':
      return <Chip appearance="success" label={status} />;
    case 'failed':
    case 'rejected':
    case 'denied':
      return <Chip appearance="error" label={status} />;
    case 'pending':
    case 'in_progress':
    case 'processing':
      return <Chip appearance="warning" label={status} />;
    case 'cancelled':
    case 'expired':
      return <Chip appearance="neutral" label={status} />;
    default:
      return <Chip appearance="neutral" label={status || 'Unknown'} />;
  }
};

const ZealAudit: React.FC = () => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [loading, setLoading] = useState(true);
  const [zealLogs, setZealLogs] = useState<ZealCheckLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedReportData, setSelectedReportData] = useState<any>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    total_count: 0,
    current_page: 1,
    per_page: 25,
    total_pages: 0,
  });
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetchZealLogs();
  }, []);

  // Debounce search with 500ms delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== query) {
        setQuery(searchInput);
        setPagination((prev) => ({ ...prev, current_page: 1 })); // Reset to first page on search
        fetchZealLogs(searchInput, 1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, query]);

  const fetchZealLogs = async (searchQuery?: string, page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: pagination.per_page.toString(),
      });

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await axiosRubyClient.get(
        `/api/zeal_check_logs?${params.toString()}`,
      );
      console.log('--------');
      console.log(response.data);
      console.log('--------');
      if (response.data) {
        setZealLogs(response.data.data || []);
        setPagination({
          total_count: response.data.pagination.total_count || 0,
          current_page: response.data.pagination.current_page || 1,
          per_page: response.data.pagination.per_page || 25,
          total_pages: response.data.pagination.total_pages || 0,
        });
      }
    } catch (err: any) {
      console.error('Error fetching zeal logs:', err);
      setError(
        err.response?.data?.message || 'Failed to fetch zeal audit logs',
      );
    } finally {
      setLoading(false);
    }
  };

  const onQueryChange = (value: string) => {
    setSearchInput(value);
  };

  const onPageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    fetchZealLogs(query, page);
  };

  const handleViewReport = (reportData: any) => {
    setSelectedReportData(reportData);
    setShowReportModal(true);
  };

  const handleCloseReport = () => {
    setShowReportModal(false);
    setSelectedReportData(null);
  };

  if (loading && zealLogs.length === 0) {
    return <LoadingState />;
  }

  return (
    <Page
      size="full"
      title="Zeal Audit"
      primaryAction={{
        a11yLabel: 'Refresh',
        label: 'Refresh',
        onAction: () => fetchZealLogs(query, pagination.current_page),
      }}
    >
      <Card>
        <Card.Section>
          <Stack gap={phoneOnly ? 16 : 8} vertical={phoneOnly}>
            <div style={{ flex: 1 }}>
              <TextInput
                id="input-search-worker"
                placeholder={`Search for GravyWorkers...`}
                value={searchInput}
                onChange={(ev) => onQueryChange(ev.currentTarget.value)}
              />
            </div>
          </Stack>
        </Card.Section>

        <Table spacing={false}>
          <TableHeader fields={HEADER_FIELDS} />
          <tbody>
            {!loading && zealLogs.length > 0 ? (
              zealLogs.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Body>{item.worker.zeal_employee_id}</Body>
                  </TableCell>
                  <TableCell>
                    <Body>
                      {`${item.worker.user.first_name} ${item.worker.user.last_name}`}
                    </Body>
                  </TableCell>
                  <TableCell>{getStatusChip(item.previous_status)}</TableCell>
                  <TableCell>{getStatusChip(item.new_status)}</TableCell>
                  <TableCell>
                    <Body>{item.notes || '-'}</Body>
                  </TableCell>
                  <TableCell>
                    <Body>
                      {format(new Date(item.created_at), 'MMM dd, yyyy HH:mm')}
                    </Body>
                  </TableCell>
                  <TableCell>
                    <IconicButton
                      a11yLabel="View report data"
                      icon={faEye}
                      onClick={() => handleViewReport(item.report_data)}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : !loading && zealLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Stack justify="center" style={{ padding: '40px 0' }}>
                    <Body>
                      {query
                        ? 'No zeal audit logs found matching your search.'
                        : 'No zeal audit logs found.'}
                    </Body>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : null}
          </tbody>
        </Table>

        {error && (
          <Card.Section>
            <Stack justify="center">
              <Body color="error">{error}</Body>
            </Stack>
          </Card.Section>
        )}

        {pagination.total_pages > 1 && (
          <Card.Section>
            <Pagination
              currentPage={pagination.current_page}
              totalPages={pagination.total_pages}
              onPageChange={onPageChange}
              totalCount={pagination.total_count}
            />
          </Card.Section>
        )}
      </Card>

      <ReportModal
        reportData={selectedReportData}
        isOpen={showReportModal}
        onClose={handleCloseReport}
      />
    </Page>
  );
};

export default ZealAudit;
